import styled, { css } from 'styled-components';
import { ReactNode } from 'react';
import { useDrfqRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import {
  AskProvider,
  BidProvider,
} from '@paradigm/features/src/unified-rfqs/ui/one-click-trade/aggress-order-action-context';
import { Side } from '@paradigm/features/src/unified-rfqs/entities/shared';
import { findDrfqBaseLegIndex } from '@paradigm/features/src/unified-rfqs/utils/legs';
import { Drfq } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import { RfqOrder } from '@paradigm/features/src/unified-rfqs/entities/drfq/orders';

import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { Container } from '#/components/UnifiedRfqs/one-click-trade/OneClickAction';
import { RowIndexProvider } from '#/components/UnifiedRfqs/components/RfqRowBase';
import ScreenPricesAction from '#/components/UnifiedRfqs/rfqs/action/ScreenPriceAction';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import {
  BoughtLabel,
  DeskLabel,
  DeskName,
  Divider,
  MainRow,
  QuoteActionContainer,
  SoldLabel,
} from '#/components/UnifiedRfqs/drfq/common';
import RfqOrders from '#/components/UnifiedRfqs/drfq/RfqOrders';
import { AnonIconText } from '#/components/UnifiedRfqs/components/WarningMessage';
import { useBboView } from '@paradigm/features/src/unified-rfqs/ui/bbo-view-context';

export function ScreenPrices() {
  const drfq = useDrfqRfq();
  const baseLegIndex = findDrfqBaseLegIndex(drfq.kind, drfq.legs);
  const productCode = drfq.legs[baseLegIndex]?.product_code;

  return <ScreenPricesAction productCode={productCode} isQtyNormalized />;
}

interface SecondaryRowOrdersProps {
  index: number;
  onPriceClick?: () => void;
  bid: RfqOrder | undefined;
  ask: RfqOrder | undefined;
}

interface PrimaryOrdersRowExecutedProps {
  bid: RfqOrder | undefined;
  ask: RfqOrder | undefined;
  executionSide: Side;
  isExpanded: boolean;
  onPriceClick?: () => void;
}

export function PrimaryOrdersRowExecuted({
  bid,
  ask,
  executionSide,
  isExpanded,
  onPriceClick,
}: PrimaryOrdersRowExecutedProps) {
  const mark = useBboView().markPrice.formatted;
  const isMobile = useIsMobile();
  const bidDesk = bid?.desk === null ? <AnonIconText /> : bid?.desk;
  const askDesk = ask?.desk === null ? <AnonIconText /> : ask?.desk;

  return (
    <RowIndexProvider value={0}>
      <BidProvider value={bid}>
        <AskProvider value={ask}>
          <MainRow isExpanded={isExpanded} isViewDetails>
            <SellContainer css="grid-area: qsell;">
              <DeskName data-testid="desk-sell">{bidDesk}</DeskName>
              {isMobile && <span />}
              {executionSide === 'SELL' ? <StyledSoldLabel /> : <span />}
            </SellContainer>
            <RfqOrders
              bid={bid}
              ask={ask}
              onPriceClick={onPriceClick}
              mark={mark}
            />
            <BuyContainer css="grid-area: qbuy;">
              {isMobile && <span />}
              {executionSide === 'BUY' ? <StyledBoughtLabel /> : <span />}
              {isMobile && <span />}
              <DeskName data-testid="desk-buy">{askDesk}</DeskName>
            </BuyContainer>
          </MainRow>
        </AskProvider>
      </BidProvider>
    </RowIndexProvider>
  );
}

function SecondaryOrdersRow({
  index,
  bid,
  ask,
  onPriceClick,
}: SecondaryRowOrdersProps) {
  const isMobile = useIsMobile();
  const bidDesk = bid?.desk === null ? <AnonIconText /> : bid?.desk;
  const askDesk = ask?.desk === null ? <AnonIconText /> : ask?.desk;
  return (
    // Secondary rows start from 1
    <RowIndexProvider value={index + 1}>
      <BidProvider value={bid}>
        <AskProvider value={ask}>
          <Divider />
          <SecondaryRow>
            <SellContainer>
              <DeskName data-testid="desk-sell">{bidDesk}</DeskName>
              <span />
            </SellContainer>
            <RfqOrders
              bid={bid}
              ask={ask}
              onPriceClick={onPriceClick}
              mark={null}
            />
            <BuyContainer>
              <span />
              {isMobile && <span />}
              {isMobile && <span />}
              <DeskName data-testid="desk-buy">{askDesk}</DeskName>
            </BuyContainer>
          </SecondaryRow>
        </AskProvider>
      </BidProvider>
    </RowIndexProvider>
  );
}

export function renderSecondaryRows({
  bids,
  asks,
  ordersLength = 2,
  onPriceClick,
  rfqId,
}: {
  bids: RfqOrder[];
  asks: RfqOrder[];
  onPriceClick?: () => void;
  ordersLength?: number;
  rfqId: Drfq['id'];
}) {
  const actions: ReactNode[] = [];

  for (let i = 0; i < ordersLength; i++) {
    const key = `rfq-${rfqId}-orders-${i}`;
    actions.push(
      <SecondaryOrdersRow
        key={key}
        onPriceClick={onPriceClick}
        index={i}
        bid={bids[i]}
        ask={asks[i]}
      />,
    );
  }

  return actions;
}

interface ConfirmableAction {
  readonly isConfirmStep?: boolean;
}

const ActionContainer = styled.div.withConfig<ConfirmableAction>({
  shouldForwardProp: (prop) => prop !== 'isConfirmStep',
})`
  align-items: center;
  height: 100%;
  display: ${(props) => (props.isConfirmStep === true ? 'block' : 'grid')};
  padding: ${(props) => (props.isConfirmStep === true ? '0' : '0 6px')};
  column-gap: 6px;

  ${(props) =>
    props.isConfirmStep !== true &&
    css`
      grid-template-columns: 1fr;

      ${Container} {
        padding: unset;
        gap: unset;
        @media (max-width: ${MOBILE_BREAKPOINT}px) {
          grid-template-columns: 1fr;
          width: 100%;
        }
      }
    `}

  ${DeskLabel} {
    width: 100%;
  }
`;

export const SellContainer = styled(ActionContainer)`
  grid-template-columns: 1fr 50px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 7fr 1fr 8fr 3.5fr;
    grid-template-areas: 'bdesk . bid mark';
    gap: 0;
    padding: 0;
  }
`;

export const BuyContainer = styled(ActionContainer)`
  grid-template-columns: 50px 1fr;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 3.5fr 8fr 1fr 7fr;
    grid-template-areas: 'mark ask . adesk';
    gap: 0;
    padding: 0;
  }
`;

const SecondaryRow = styled(QuoteActionContainer)`
  height: auto;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: 80px;
    padding: 0.5rem 1rem;
  }
`;

const StyledBoughtLabel = styled(BoughtLabel)`
  text-align: left;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    text-align: center;
  }
`;

const StyledSoldLabel = styled(SoldLabel)`
  text-align: right;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    text-align: center;
  }
`;
